import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  Menu, Route as RouteIcon, CheckSquare, Clock, Palmtree, Receipt, FileText, BookOpen, User, LogOut, ChevronRight, PackageCheck,
} from "lucide-react";

let _capsCache = null;
/** Capacidades RRHH del usuario (misma derivación que backend services/rrhh_capacidades). */
export function useCapacidadesRRHH() {
  const [caps, setCaps] = useState(_capsCache);
  useEffect(() => {
    if (_capsCache) return;
    api.get("/portal-empleado/capacidades").then((r) => { _capsCache = r.data; setCaps(r.data); }).catch(() => setCaps({ tipo: "externo" }));
  }, []);
  return caps;
}

export function opcionesMenuEmpleado({ caps, hasPermission, roles = [] }) {
  const interno = caps?.tipo === "interno";
  const esMontador = roles.includes("montador") || roles.includes("montador_externo") || roles.includes("logistica");
  const items = [];
  if (esMontador) items.push({ to: "/mi-ruta", label: "Mis rutas", sub: "Rutas y trabajos asignados", icon: RouteIcon, color: "text-amber-400" });
  items.push({ to: "/tareas", label: "Mis tareas", sub: "Tareas pendientes", icon: CheckSquare, color: "text-amber-400" });
  if (caps?.fichaje !== false) items.push({ to: "/fichaje", label: "Fichaje horario", sub: "Entrada / salida con GPS", icon: Clock, color: "text-emerald-400" });
  items.push(interno
    ? { to: "/rrhh", label: "Portal del empleado", sub: "Horas · fichajes · solicitudes · documentos", icon: Palmtree, color: "text-indigo-300", testid: "menu-portal-empleado" }
    : { to: "/rrhh", label: "Mi jornada", sub: "Fichajes y correcciones", icon: Palmtree, color: "text-indigo-300", testid: "menu-mi-jornada" });
  if (hasPermission?.("logistica:recepcion")) items.push({ to: "/logistica-entrada", label: "Recepción de material", sub: "Marcar recibido e imprimir etiqueta", icon: PackageCheck, color: "text-blue-400" });
  items.push({ to: "/gastos", label: "Gastos y compras", sub: "Tickets y gastos de ruta", icon: Receipt, color: "text-zinc-300" });
  if (!interno) items.push({ to: "/facturas-externos", label: "Facturación (recibidas)", sub: "Tus facturas a DNF", icon: FileText, color: "text-zinc-300" });
  items.push({ to: "/manuales", label: "Manuales de usuario", sub: "Ayuda paso a paso", icon: BookOpen, color: "text-zinc-300" });
  items.push({ to: "/mis-acreditaciones", label: "Mis acreditaciones", sub: "Carnets y certificados", icon: User, color: "text-zinc-300" });
  items.push({ to: "/perfil", label: "Mi perfil", sub: "Datos y contraseña", icon: User, color: "text-zinc-300" });
  return items;
}

/** Botón visible "Menú" (icono + texto) que abre el menú del empleado con las opciones permitidas. */
export function MenuEmpleadoButton({ className = "" }) {
  const [open, setOpen] = useState(false);
  const { user, logout, hasPermission } = useAuth();
  const caps = useCapacidadesRRHH();
  const nav = useNavigate();
  const loc = useLocation();
  const items = opcionesMenuEmpleado({ caps, hasPermission, roles: user?.roles || [] });
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="menu-empleado-btn" aria-label="Abrir menú"
        className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-zinc-700 bg-zinc-900/70 hover:bg-zinc-800 text-zinc-100 text-sm font-medium ${className}`}>
        <Menu className="w-4 h-4" /> Menú
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="bg-[#0c0c10] border-zinc-800 text-zinc-100 w-[86vw] max-w-sm p-0" data-testid="menu-empleado-sheet">
          <SheetHeader className="px-4 py-4 border-b border-zinc-800 text-left">
            <SheetTitle className="text-zinc-100">{user?.name}</SheetTitle>
            <SheetDescription className="text-zinc-500 text-xs">{caps?.tipo === "interno" ? "Personal interno" : "Colaborador externo"} · {user?.email}</SheetDescription>
          </SheetHeader>
          <nav className="p-2 overflow-y-auto max-h-[calc(100vh-150px)]" aria-label="Menú del empleado">
            {items.map((it) => {
              const activo = loc.pathname === it.to;
              return (
                <button key={it.to} onClick={() => { setOpen(false); nav(it.to); }} data-testid={it.testid || `menu-item-${it.to.replace(/\//g, "")}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-zinc-900 ${activo ? "bg-zinc-900 ring-1 ring-amber-500/40" : ""}`}>
                  <it.icon className={`w-5 h-5 shrink-0 ${it.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-100">{it.label}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{it.sub}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-600" />
                </button>
              );
            })}
          </nav>
          <div className="p-3 border-t border-zinc-800">
            <button onClick={async () => { await logout(); nav("/login"); }} data-testid="menu-logout"
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-red-500/10 hover:text-red-300 text-sm">
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
